import { PricingFormDefaultValues, PricingItemType, PricingTitle } from "@/types/PricingForm"
import { PricingOptionsType, PricingPlansAfterFormatType, PricingPlansResponseType } from "@/types/api/pricingPlan"
import {EnterpriseFormDefaultValues, EnterpriseFormType} from "@/types/EnterpriseForm"
import { LegalPersonFormDefaultValues } from "@/types/LegalPersonForm"
import { AllStepFormType } from "@/types/StepForm"
import { PlanType } from "@/types/PlansType"
import { format } from "date-fns";
/**
 *
 * @param item array of pricing options where users checked some of them
 * @returns number: total cost from the checked pricing options
 */
export const totalFromPricingOptions = (item: PricingItemType[]) => {
  return item
    .filter((pricingOption) => pricingOption.requested === true)
    .reduce((prev, curr) => prev + (curr.value ?? 0), 0)
}

export const choosedPricingOptions = (item: PricingItemType[]) => {
  return item
    .filter((pricingOption) => pricingOption.requested === true)
    .map((pricingOption) => pricingOption.id)
}

export const orderOptionByStatus = (item: PricingItemType[]) => {
  const available = []
  const unavailable = []
  const checkable = []
  for (const option of item) {
    if (option.status === "available") {
      available.push(option)
    } else if (option.status === "checkable") {
      checkable.push(option)
    } else {
      unavailable.push(option)
    }
  }

  return available.concat(checkable, unavailable)
}

export const transformDataIntoFormData = <T>(
  data: AllStepFormType,
  DefaultData: T,
) => {
  let formData = new FormData()
  for (const [key, value] of Object.entries(data)) {
    // console.log(key, value)
    if (key in DefaultData) {
      formData.append(key, value)
    }
  }

  return formData
}

export const getPricingPlanData = (data: AllStepFormType) => {
  let pricingPlan = PricingFormDefaultValues
  if ("plan" in data) {
    pricingPlan = {
      plan: data.plan,
      // chosen_options: data.chosen_options,
      has_reduction: data.has_reduction,
      reductions: {
        collabo: {
          value: data.collabo_reduction ?? undefined,
          start_date: data.collabo_reduction_start ?? undefined,
          end_date: data.collabo_reduction_end ?? null,
          type: data.collabo_reduction_type ?? undefined,
        },
        subscription: {
          value: data.subscription_reduction ?? undefined,
          start_date: data.subscription_reduction_start ?? undefined,
          end_date: data.subscription_reduction_end ?? null,
          type: data.subscription_reduction_type ?? undefined,
        },
        monthly: {
          value: data.monthly_reduction ?? undefined,
          start_date: data.monthly_reduction_start ?? undefined,
          end_date: data.monthly_reduction_end ?? null,
          type: data.monthly_reduction_type ?? undefined,
        },
      },
      provision: data.provision,
      base_amount: data.base_amount,
      per_user: data.per_user,
      max_users: data.max_users,
    }
  }

  return pricingPlan
}

export const transformDataBasedOnActiveStep = (
  activeStep: number,
  data: AllStepFormType,
) : FormData => {
  switch (activeStep) {
    case 1: {
      const vps_end_date = data["vps_end_date"] ? format(data["vps_end_date"],"yyyy-MM-dd") : ""
      return transformDataIntoFormData({...data, vps_end_date} as EnterpriseFormType, EnterpriseFormDefaultValues)
    }

    case 2: {
      return transformDataIntoFormData(data, LegalPersonFormDefaultValues)
    }

    default: {
      return getPricingPlanData(data)
    }
  }
}

export const matchPlanTitleWithName = (
  name: PricingPlansResponseType["name"] | string,
): PricingTitle | string => {
  switch (name) {

    case "STARTER_5": {
      return "Starter 5"
    }

    case "STARTER_10": {
      return "Starter 10"
    }

    case "PREMIUM": {
      return "Premium"
    }

    case "STANDARD": {
      return "Standard"
    }

    case "CUSTOM": {
      return "Personnalisé"
    }
    default:
      return name
  }
}

export const matchPlanOptionWithType = (
  type: PricingOptionsType["type"],
): PricingItemType["status"] => {
  switch (type) {
    case 1: {
      return "available"
    }

    case 2: {
      return "checkable"
    }

    case 3: {
      return "unavailable"
    }
  }
}

export const transformAPIDataToOptionsData = (
  data: PricingOptionsType,
): PricingItemType => {
  return {
    id: data.option.id,
    label: data.option.label,
    value: data.option.amount,
    status: matchPlanOptionWithType(data.type),
    requested: data.type === 2 ? false : undefined,
  }
}

export const transformAPIDataToPricingPlanData = (
  data: PlanType[],
): PricingPlansAfterFormatType[] => {
  const plans: PricingPlansAfterFormatType[] = data.map((plan, index) => ({
    title: matchPlanTitleWithName(plan.name),
    id: plan.id,
    subTitle: "",
    description: plan.description,
    items: orderOptionByStatus(plan.options.map(transformAPIDataToOptionsData)),
  }))
  return plans
}
